/** Canonical role ids used in JWT / tenant member storage. */
export const ADMIN_ROLE = "admin";
export const TENANT_ADMIN_ROLE = "tenant-admin";
export const CSR_ROLE = "csr";
export const PROD_SUPPORT_ADMIN_ROLE = "prod-support-admin";
export const USER_ROLE = "user";

const SUPER_ADMIN_ALIASES = [
  ADMIN_ROLE,
  "sami_admin",
  "sami_x_admin",
  "sami-x-admin",
] as const;

const CSR_ALIASES = [
  CSR_ROLE,
  "prod-support",
  "prod_support",
  "prodsupport",
  "customer-service-representative",
  "customer_service_rep",
  "customer_service_representative",
] as const;

const PROD_SUPPORT_ADMIN_ALIASES = [
  PROD_SUPPORT_ADMIN_ROLE,
  "production-support-admin",
  "prod_support_admin",
] as const;

const TENANT_ADMIN_ALIASES = [
  TENANT_ADMIN_ROLE,
  "tenant_admin",
  "tenantadmin",
  ADMIN_ROLE,
  "sami_admin",
] as const;

const END_USER_ALIASES = [USER_ROLE, "merchant"] as const;

const PRODUCT_MANAGER_ALIASES = [
  "product-manager",
  "product_manager",
  "productmanager",
] as const;

const ARCHITECT_ALIASES = ["architect", "Architect"] as const;

const GATEWAY_DEVELOPER_ALIASES = [
  "gateway-developer",
  "gateway_developer",
  "gatewaydeveloper",
] as const;

const AI_PRODUCT_MANAGER_ALIASES = [
  "ai-product-manager",
  "ai_product_manager",
  "aiproductmanager",
  "AI Product Manager",
] as const;

const PLATFORM_ADMIN_ALIASES = [
  "platform-admin",
  "platform_admin",
  "platformadmin",
] as const;

export function normalizeRole(role: string): string {
  return role.trim().toLowerCase();
}

export function userHasRole(userRoles: string[], requiredRole: string): boolean {
  const target = normalizeRole(requiredRole);
  return userRoles.some((r) => normalizeRole(r) === target);
}

export function userHasAnyRole(
  userRoles: string[] | undefined | null,
  aliases: readonly string[],
): boolean {
  if (!userRoles?.length) return false;
  return aliases.some((role) => userHasRole(userRoles, role));
}

/** Every logged-in user is treated as having the baseline user role. */
export function withBaselineUserRole(
  userRoles: string[] | undefined | null,
): string[] {
  const merged = userRoles?.length ? [...userRoles] : [];
  if (!merged.some((r) => normalizeRole(r) === USER_ROLE)) {
    merged.push(USER_ROLE);
  }
  return merged;
}

export function isSuperAdmin(userRoles: string[] | undefined | null): boolean {
  return userHasAnyRole(userRoles, SUPER_ADMIN_ALIASES);
}

function withRoles(userRoles: string[] | undefined | null): string[] {
  return withBaselineUserRole(userRoles);
}

export function isProductionSupportAdmin(
  userRoles: string[] | undefined | null,
): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, PROD_SUPPORT_ADMIN_ALIASES);
}

export function isCsr(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, CSR_ALIASES) || isProductionSupportAdmin(roles);
}

export function isTenantAdmin(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, TENANT_ADMIN_ALIASES);
}

export function isProductManager(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, PRODUCT_MANAGER_ALIASES);
}

export function isArchitect(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, ARCHITECT_ALIASES);
}

export function isGatewayDeveloper(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, GATEWAY_DEVELOPER_ALIASES);
}

/** Architect, product manager, or gateway developer — gateway route hub ACL */
export function canManageGatewayRoutes(userRoles: string[] | undefined | null): boolean {
  return (
    isArchitect(userRoles) ||
    isProductManager(userRoles) ||
    isGatewayDeveloper(userRoles)
  );
}

/** Architect or product manager — Kafka event registry hub ACL */
export function canAccessKafkaEventHub(
  userRoles: string[] | undefined | null,
): boolean {
  return isArchitect(userRoles) || isProductManager(userRoles);
}

/** Architect or product manager — API Hub co-ownership */
export function canManageApiHub(
  userRoles: string[] | undefined | null,
): boolean {
  return isArchitect(userRoles) || isProductManager(userRoles);
}

/** Architect, product manager, or gateway developer — API Explorer standards review tab */
export function canViewStandardsReview(
  userRoles: string[] | undefined | null,
): boolean {
  return (
    isArchitect(userRoles) ||
    isProductManager(userRoles) ||
    isGatewayDeveloper(userRoles)
  );
}

export function isAiProductManager(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, AI_PRODUCT_MANAGER_ALIASES);
}

export function isPlatformAdmin(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, PLATFORM_ADMIN_ALIASES);
}

/** Registered end-user (user and merchant are equivalent). */
export function isEndUser(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, END_USER_ALIASES);
}

const ENVIRONMENT_MANAGER_ALIASES = [
  "environment-manager",
  "environment_manager",
  "env-manager",
  "envmanager",
] as const;

const DEVOPS_OPERATOR_ALIASES = [
  "devops-operator",
  "devops_operator",
  "devops",
] as const;

export function isEnvironmentManager(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, ENVIRONMENT_MANAGER_ALIASES);
}

export function isDevopsOperator(userRoles: string[] | undefined | null): boolean {
  const roles = withRoles(userRoles);
  if (isSuperAdmin(roles)) return true;
  return userHasAnyRole(roles, DEVOPS_OPERATOR_ALIASES);
}

export function canAccessDevopsWorkspace(
  userRoles: string[] | undefined | null,
): boolean {
  return isEnvironmentManager(userRoles) || isDevopsOperator(userRoles);
}
